/**
 * **Nachhalten am Befund** — der Knopf, die Zahl daneben und das Formular
 * dahinter (Glossar, „Nachhalten"; ADR-0012).
 *
 * Sie stehen an einer Stelle, weil zwei Flächen dasselbe anbieten: der
 * **Befundverlauf** einer Revision und die **Gegenüberstellung**. Zwei Kopien
 * wären zweimal die Gelegenheit, dieselbe Sache verschieden zu beschriften — und
 * die Beschriftung ist hier die ganze Erklärung dessen, was der Knopf tut.
 *
 * **Was er nicht tut**, steht im Formular: Es entsteht kein Vorschlag, kein
 * Prüfauftrag und kein Modellaufruf. Der Vorgang — Vorschlag, Gespräch, Abnahme
 * — ist v2 und hat dort sein eigenes Gatter (ADR-0012). Wer den Knopf drückt,
 * soll wissen, dass er einen Zettel schreibt und keine Maschine anwirft.
 *
 * **Auf einem Kontrollbefund gibt es ihn nicht.** Er *ist* schon ein Muster; die
 * Fläche, die ihn zeigt, bietet ihn deshalb dort gar nicht erst an — hier steht
 * kein Zweig darauf, denn ein Knopf, der beim Drücken „geht nicht" sagt, ist die
 * schlechtere Auskunft als keiner.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { Button, Dialog, IconButton, Modal, StatusPill } from "../../design-system";
import type { FindingOrigin, FollowUpTally } from "../../api/client";

/** Was am Knopf steht, solange die eigene Person nichts vermerkt hat. */
const MARK = "Nachhalten";

/** Und was danach — die Rücknahme ist in v1.2 unbedingt (ADR-0012). */
const WITHDRAW = "Nachhalten zurücknehmen";

const EXPLANATION =
  "Vermerken Sie, dass die Erkennung dafür künftig sensibilisiert sein soll. Es " +
  "entsteht kein Vorschlag, kein Prüfauftrag und kein Modellaufruf — nur dieser " +
  "Vermerk samt Ihrer Begründung, und er wartet auf einen Menschen.";

/**
 * An welcher Herkunft nachgehalten werden kann.
 *
 * **Positiv aufgezählt und nicht als Ausschluss des Kontrollbefundes:** Ein
 * `!== "pattern"` ließe eine vierte Herkunft stillschweigend durch, und die
 * Frage, ob an ihr nachgehalten wird, hätte dann niemand gestellt. Auf einem
 * **Agentenbefund** ausdrücklich ja — die Einschränkung aus ADR-0012 betrifft
 * den Ausgang des Vorgangs in v2 und nicht den Vermerk. Dieselbe Aufzählung
 * steht im Server, und dort entscheidet sie.
 */
export function followable(origin: FindingOrigin): boolean {
  return origin === "agent" || origin === "person";
}

/** Der Vermerkstand eines Befundes, an dem noch niemand etwas vermerkt hat. */
export const UNMARKED: FollowUpTally = { findingId: 0, total: 0, mine: false };

/**
 * Der Vermerkstand eines Befundes aus der Liste, die neben den Befunden steht.
 *
 * Was dort fehlt, ist unvermerkt: Der Server schickt nur die Befunde mit, an
 * denen etwas hängt.
 */
export function followUpOf(marks: FollowUpTally[], findingId: number): FollowUpTally {
  return marks.find((one) => one.findingId === findingId) ?? UNMARKED;
}

/**
 * Der Knopf samt der Zahl daneben.
 *
 * Die Zahl steht nur da, wo etwas dahintersteht: „von 2 nachgehalten" ist eine
 * Auskunft, „von 0 nachgehalten" wäre Rauschen an jedem Befund. Dass zwei
 * Menschen dasselbe nachhalten wollen, ist ein **Signal** und keine Dublette —
 * darum die Zahl und nicht ein bloßes Häkchen.
 */
export function FollowUp({
  tally,
  busy,
  onMark,
  onWithdraw,
}: {
  tally: FollowUpTally;
  busy: boolean;
  onMark: () => void;
  onWithdraw: () => void;
}): ReactElement {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-3)" }}>
      {tally.total === 0 ? null : (
        <StatusPill tone="amber">
          {tally.total === 1 ? "nachgehalten" : `${String(tally.total)}× nachgehalten`}
        </StatusPill>
      )}

      <IconButton
        icon="flag"
        tip={tally.mine ? WITHDRAW : MARK}
        size="sm"
        // **Die Sperre steht am Aufrufort.** Der Baustein des Skills führt kein
        // `disabled` — ohne `onClick` geht der Druck ins Leere, und die Ruhefarbe
        // des Ausgegrauten ist dieselbe, die der Textknopf dafür führt
        // (`--fg-9`). Ein zweiter Vermerk, während der erste läuft, wäre sonst
        // ein zweiter Weg zum Server.
        tone="quiet"
        style={busy ? { color: "var(--fg-9)", cursor: "default" } : undefined}
        onClick={busy ? undefined : tally.mine ? onWithdraw : onMark}
      />
    </span>
  );
}

/**
 * Das Formular als Dialog: **ein Feld, und es ist Pflicht.**
 *
 * Die Begründung ist der ganze Ertrag gegenüber einer bloßen Zahl — in v2 soll
 * nicht nur der Befund vorliegen, sondern die Absicht des Menschen, festgehalten
 * in dem Moment, in dem er sie hatte. Ohne sie bleibt der Knopf grau; ein
 * Vermerk, der ohne Begründung abginge und danach vom Server abgewiesen würde,
 * wäre zweimal derselbe Weg.
 */
export function FollowUpDialog({
  title,
  busy,
  onClose,
  onSave,
}: {
  /** Der Befund, um den es geht — als Zeile über dem Feld. */
  title: string;
  busy: boolean;
  onClose: () => void;
  onSave: (reason: string) => void;
}): ReactElement {
  const [reason, setReason] = useState("");
  const written = reason.trim();

  return (
    <Modal onClose={onClose}>
      <Dialog
        title={MARK}
        description={`${EXPLANATION} ${title}`}
        width={640}
        onClose={onClose}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={busy || written === ""}
              onClick={() => onSave(written)}
            >
              Vermerk anlegen
            </Button>
          </>
        }
      >
        <textarea
          aria-label="Begründung"
          rows={4}
          value={reason}
          placeholder="Warum soll so etwas künftig auffallen?"
          onChange={(event) => setReason(event.target.value)}
          className="erp-scroll"
          style={{
            width: "100%",
            padding: "var(--sp-3) var(--sp-4)",
            background: "var(--surface-field)",
            border: "1px solid var(--border-field)",
            borderRadius: "var(--r-field)",
            color: "var(--fg-1)",
            fontFamily: "var(--font-sans)",
            fontSize: "var(--fs-12)",
            lineHeight: "var(--lh-normal)",
            resize: "vertical",
            outline: "none",
          }}
        />
      </Dialog>
    </Modal>
  );
}
